


import React, { useState } from "react";
import { Box, Typography, Tabs, Tab, useMediaQuery } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTheme as useMuiTheme } from "@mui/material/styles";

import AdminHealthContent        from "../HealthPackages/Adminhealthcontent";
import NursingPhysioServicesAdmin from "../NursingPhysiotheraphy/Nursingphysioservicesadmin";
import AyujaConfigSettings        from "../MedicineDetailsPages/AyujaConfigSettings";
import  bg from "../Logos/med_icon3.png"

// ─── Theme ───────────────────────────────────────────────────────────────────
const hubTheme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: "#006D77", light: "#00949f", dark: "#004d55", contrastText: "#fff" },
    secondary:  { main: "#E29578" },
    background: { default: "#F1F5F9", paper: "#ffffff" },
    text:       { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {
    fontFamily: "'DM Sans', 'Inter', sans-serif",
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 48 } } },
  },
});

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { label: "Health Camps & Packages", emoji: "🏕️", description: "Manage health camps and diagnostic packages" },
  { label: "Nursing & Physiotherapy", emoji: "🏥", description: "Manage nursing and physiotherapy services" },
  { label: "Pricing Configuration",   emoji: "⚙️", description: "Configure AYUJA discount and profit settings" },
];
const DRAWER_WIDTH   = 280;
const MOBILE_BAR_H   = 56;

const STRIP_OFFSET = {
  "& > *": {
    marginLeft:  "0 !important",
    paddingTop:  "0 !important",
    width:       "100% !important",
    maxWidth:    "100% !important",
  },
};
// ─── AyujaConfigSettings wrapper ─────────────────────────────────────────────
const CONFIG_STRIP = {
  display: "flex",
  justifyContent: "center",
  px: { xs: 2, sm: 4 },
  py: { xs: 3, sm: 4 },
  "& > div, & > div > div": {
    marginLeft: "0 !important",
    maxWidth:   "520px !important",
    width:      "100% !important",
    boxShadow:  "none !important",
  },
};

export default function HealthServicesManagement() {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);

  return (
    <ThemeProvider theme={hubTheme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* ── Outer shell: offset for the fixed sidebar + mobile AppBar ── */}
      <Box sx={{
        minHeight:   "100vh",
        bgcolor:     "#F1F5F9",
        ml:          { xs: 0, md: `${DRAWER_WIDTH}px` },
        pt:          { xs: `${MOBILE_BAR_H}px`, md: 0 },
        width:       { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
        boxSizing:   "border-box",
        overflowX:   "hidden",
      }}>

        {/* ── Gradient banner + tab bar ── */}
        <Box sx={{
          backgroundImage: `
            linear-gradient(135deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.68) 100%),
            url(${bg})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat" ,
          px: { xs: 2, sm: 4 },
          pt: { xs: 2.5, sm: 3 },
          pb: 0,
        }}>
          <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: { xs: 17, sm: 21, md: 24 }, color: "#fff" }}>
            🩺 Health Services Management
          </Typography>
          <Typography sx={{ fontSize: { xs: 11, sm: 13 }, color: "rgba(255,255,255,0.55)", mt: 0.4, mb: 2 }}>
            Manage health camps, nursing &amp; physiotherapy services, and pricing configuration
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: "#80ED99", height: 3, borderRadius: "3px 3px 0 0" },
              "& .MuiTab-root":       { color: "rgba(255,255,255,0.55)", "&.Mui-selected": { color: "#fff" } },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} disableRipple
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: { xs: 15, sm: 17 }, lineHeight: 1 }}>{t.emoji}</Typography>
                    <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: activeTab === i ? 800 : 600, fontSize: { xs: 12, sm: 13, md: 14 }, whiteSpace: "nowrap" }}>
                      {t.label}
                    </Typography>
                  </Box>
                }
                sx={{ minHeight: 44, px: { xs: 1.5, sm: 2.5 } }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab 0 — Health Camps & Packages */}
        <Box sx={{ display: activeTab === 0 ? "block" : "none", ...STRIP_OFFSET }}>
          <AdminHealthContent />
        </Box>

        {/* Tab 1 — Nursing & Physiotherapy */}
        <Box sx={{ display: activeTab === 1 ? "block" : "none", ...STRIP_OFFSET }}>
          <NursingPhysioServicesAdmin />
        </Box>

        {activeTab === 2 && (
          <Box sx={{ ...CONFIG_STRIP }}>
            <AyujaConfigSettings />
          </Box>
        )}

      </Box>
    </ThemeProvider>
  );
}